import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as platformAdminApi from '../services/platformAdminApi';
import { getPlatformAdminToken } from '../services/platformAdminApiClient';
import Card from '../components/Card';
import Button from '../components/Button';
import FormField, { inputStyle } from '../components/FormField';
import DateInput from '../components/DateInput';

const STATUS_COLORS = {
  active: 'var(--color-success)',
  trial: 'var(--color-accent)',
  suspended: 'var(--color-danger)',
  expired: 'var(--color-text-muted)',
};

function StatusBadge({ status }) {
  return (
    <span
      className="data-figure"
      style={{
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        color: STATUS_COLORS[status] || 'var(--color-text-muted)',
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

function AddCompanyForm({ onCreated }) {
  const [form, setForm] = useState({
    companyName: '', adminFullName: '', adminEmail: '', adminPassword: '',
    licenseStatus: 'trial', maxUsers: 5, licenseExpiresAt: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await platformAdminApi.createCompany({
        ...form,
        maxUsers: Number(form.maxUsers),
        licenseExpiresAt: form.licenseExpiresAt || null,
      });
      setForm({
        companyName: '', adminFullName: '', adminEmail: '', adminPassword: '',
        licenseStatus: 'trial', maxUsers: 5, licenseExpiresAt: '',
      });
      onCreated();
    } catch (err) {
      setError(err.message || 'Could not create company.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="Add company">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
          <FormField label="Company name">
            <input required style={inputStyle} value={form.companyName} onChange={set('companyName')} />
          </FormField>
          <FormField label="Admin full name">
            <input required style={inputStyle} value={form.adminFullName} onChange={set('adminFullName')} />
          </FormField>
          <FormField label="Admin email">
            <input type="email" required style={inputStyle} value={form.adminEmail} onChange={set('adminEmail')} />
          </FormField>
          <FormField label="Admin temporary password">
            <input type="password" required style={inputStyle} value={form.adminPassword} onChange={set('adminPassword')} />
          </FormField>
          <FormField label="License status">
            <select style={inputStyle} value={form.licenseStatus} onChange={set('licenseStatus')}>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
            </select>
          </FormField>
          <FormField label="Max users">
            <input type="number" min="1" required style={inputStyle} value={form.maxUsers} onChange={set('maxUsers')} />
          </FormField>
          <FormField label="License expires (optional)">
            <DateInput style={inputStyle} value={form.licenseExpiresAt} onChange={set('licenseExpiresAt')} />
          </FormField>
        </div>
        {error && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{error}</p>}
        <Button type="submit" disabled={submitting}>{submitting ? 'Creating...' : 'Create company'}</Button>
      </form>
    </Card>
  );
}

function LicenseEditor({ company, onSaved, onCancel }) {
  const [status, setStatus] = useState(company.licenseStatus);
  const [plan, setPlan] = useState(company.licensePlan || '');
  const [maxUsers, setMaxUsers] = useState(company.maxUsers);
  const [expiresAt, setExpiresAt] = useState(company.licenseExpiresAt ? company.licenseExpiresAt.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await platformAdminApi.updateLicense(company.companyId, {
        licenseStatus: status,
        licensePlan: plan || null,
        maxUsers: Number(maxUsers),
        licenseExpiresAt: expiresAt || null,
      });
      onSaved();
    } catch (err) {
      setError(err.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr style={{ background: 'var(--color-surface-alt)' }}>
      <td colSpan={6} style={{ padding: 'var(--space-3)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FormField label="Status">
            <select style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
            </select>
          </FormField>
          <FormField label="Plan (optional label)">
            <input style={inputStyle} value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="e.g. Pro" />
          </FormField>
          <FormField label="Max users">
            <input type="number" min="1" style={inputStyle} value={maxUsers} onChange={(e) => setMaxUsers(e.target.value)} />
          </FormField>
          <FormField label="Expires">
            <DateInput style={inputStyle} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </FormField>
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            <Button variant="text" onClick={onCancel}>Cancel</Button>
          </div>
        </div>
        {error && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{error}</p>}
      </td>
    </tr>
  );
}

export default function PlatformAdminDashboardPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const items = await platformAdminApi.listCompanies();
      setCompanies(items);
      setError(null);
    } catch (err) {
      if (err.code === 'AUTH_REQUIRED' || err.message === undefined) {
        navigate('/platform-admin/login', { replace: true });
        return;
      }
      setError(err.message || 'Could not load companies.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getPlatformAdminToken()) {
      navigate('/platform-admin/login', { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const handleToggleStatus = async (company) => {
    setBusyId(company.companyId);
    try {
      if (company.licenseStatus === 'suspended') {
        await platformAdminApi.activateCompany(company.companyId);
      } else {
        await platformAdminApi.suspendCompany(company.companyId);
      }
      await load();
    } catch (err) {
      setError(err.message || 'Could not update status.');
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = () => {
    platformAdminApi.logout();
    navigate('/platform-admin/login', { replace: true });
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'var(--space-5)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-5)' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>
            Platform Admin
          </div>
          <h1 style={{ margin: 0 }}>Companies</h1>
        </div>
        <Button variant="text" onClick={handleLogout}>Log out</Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <Card title={`All companies (${companies.length})`} style={{ marginBottom: 'var(--space-5)' }}>
        {loading ? <p style={{ color: 'var(--color-text-secondary)' }}>Loading...</p> : (
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                  {['Company', 'Status', 'Plan', 'Users', 'Expires', ''].map((h) => (
                    <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <React.Fragment key={c.companyId}>
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{c.name}</td>
                      <td style={{ padding: '8px 4px' }}><StatusBadge status={c.licenseStatus} /></td>
                      <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{c.licensePlan || '\u2014'}</td>
                      <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                        {c.userCount} / {c.maxUsers}
                      </td>
                      <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                        {c.licenseExpiresAt ? c.licenseExpiresAt.slice(0, 10) : '\u2014'}
                      </td>
                      <td style={{ padding: '8px 4px', whiteSpace: 'nowrap' }}>
                        <Button variant="text" onClick={() => setEditingId(editingId === c.companyId ? null : c.companyId)}>
                          {editingId === c.companyId ? 'Close' : 'Edit'}
                        </Button>
                        <Button
                          variant="text"
                          disabled={busyId === c.companyId}
                          onClick={() => handleToggleStatus(c)}
                        >
                          {c.licenseStatus === 'suspended' ? 'Activate' : 'Suspend'}
                        </Button>
                      </td>
                    </tr>
                    {editingId === c.companyId && (
                      <LicenseEditor
                        company={c}
                        onSaved={() => { setEditingId(null); load(); }}
                        onCancel={() => setEditingId(null)}
                      />
                    )}
                  </React.Fragment>
                ))}
                {companies.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No companies yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AddCompanyForm onCreated={load} />
    </div>
  );
}
